import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

import styles from "../../../styles/patient-page-modules/orderDeviceModal.module.css";
import Spinner from "../../../components/spinner";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { orderDropship } from "../../../services/patient/devices";
import { getPatientGeneralDetails } from "../../../services/patient/details";
import { getStates } from "../../../services/location";
import toast from "react-hot-toast";
import Button from "../../button-v1";
import { states } from "../../../utils/constants";
const devices = [
  { label: "Weight Scale", value: "Tenovi Scale" },
  { label: "Tenovi Watch", value: "Tenovi Watch" },
  { label: "Blood Pressure Monitor", value: "Tenovi Cellular BPM - L" },
  { label: "Bariatric Scale", value: "Bariatric Scale" },
  { label: "Peak Flow Meter", value: "Tenovi Peak Flow Meter" },
  { label: "Cellular Glucometer", value: "Tenovi Cellular Glucometer" },
  { label: "Cellular Scale", value: "Tenovi Cellular Scale" },
  { label: "Pulse Oximeter", value: "Tenovi Pulse Ox" },
  { label: "Blood Presure monitor (Large)", value: "Tenovi BPM - L" },
  { label: "Cellular Pulse Oximeter", value: "Tenovi Cellular Pulse Ox" },
  { label: "Glucometer", value: "Tenovi Glucometer" },
  { label: "Thermometer", value: "Tenovi Thermometer" },
  {
    label: "Cellular Blood Pressure Monitor",
    value: "Tenovi Cellular BPM - S",
  },
  { label: "Blood Presure monitor (Small)", value: "Tenovi BPM - S" },
  { label: "Omron Wrist Blood Pressure Monitor (7 Series)", value: "Omron Wrist BPM - 7 Series" },
];

const schema = {
  shipping_first_name: "",
  shipping_last_name: "",
  shipping_address: "",
  shipping_city: "",
  shipping_state: "",
  shipping_zip_code: "",
  required_signature: false,
  ship_gateway_only: false,
  device_name: "",
  notes: "",
};

const validationSchema = z.object({
  shipping_first_name: z.string().min(1, "First name is required"),
  shipping_last_name: z.string().min(1, "Last name is required"),
  shipping_address: z.string().min(1, "Address is required"),
  shipping_city: z.string().min(1, "City is required"),
  shipping_state: z.string().min(1, "State is required"),
  shipping_zip_code: z.string().min(5, "Zip code is required"),
  required_signature: z.boolean().default(false),
  ship_gateway_only: z.boolean().default(false),
  device_name: z.string().min(1, "Device name is required"),
  notes: z.string().optional(),
});

const OrderDeviceModal = ({ isOpen, onClose, currentPatient }) => {
  const queryClient = useQueryClient();
  const [tenoviDevices, setTenoviDevices] = useState([]);
  const [selectedForm, setSelectedForm] = useState("tenovi");
  const [loading, setLoading] = useState(true);
  const modalRef = useRef(null);
  const [step, setStep] = useState(1);
  const [pendingValues, setPendingValues] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: schema,
    resolver: zodResolver(validationSchema),
  });

  const {
    data: patient,
    error,
    isLoading: isPatientLoading,
    refetch,
  } = useQuery(["patient-general-details"], () => getPatientGeneralDetails());

  const {
    mutate: orderNewDeviceMutation,
    isLoading: orderDeviceMutationLoading,
  } = useMutation((data) => orderDropship(data), {
    onSuccess: () => {
      toast.success("Order placed successfully");
      queryClient.invalidateQueries(["patient-dropshied-ordered-devices"]);
      onClose();
    },
    onError: (error) =>
      toast.error(error?.response?.data?.error || "Could not place order"),
  });

  useEffect(() => {
    if (isPatientLoading) return;
    const user = patient?.user;
    setValue("shipping_first_name", user?.first_name);
    setValue("shipping_last_name", user?.last_name);
    setValue("shipping_address", user?.address_line_1);
    setValue("shipping_city", user?.city);
    setValue("shipping_state", user?.state);
    setValue("shipping_zip_code", user?.postal_code);
  }, [patient]);

  const submitHandler = (values) => {
    // Move to confirmation step with captured values
    setPendingValues(values);
    setStep(2);
  };

  const shipping_state = watch("shipping_state");
  const device_name = watch("device_name");

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Order Device</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isPatientLoading ? (
            <p className="content-center">
              <Spinner />
            </p>
          ) : step === 2 ? (
            <div className={styles.input_wrapper}>
              <h3>Confirm Order</h3>
              <p>Are you sure you want to place this order?</p>
            </div>
          ) : (
            <form className={styles.input_wrapper}>
              <small>
                Patient Name: {patient?.user?.first_name}{" "}
                {patient?.user?.last_name}
              </small>
              <div className={styles.shipping_info}>
                <h3>Shipping Information</h3>
                <small>
                  Update Shipping info in patient settings to make changes
                </small>
              </div>

              <div className={styles.input_group}>
                <div className={styles.input_field}>
                  <label>First Name</label>
                  <input
                    type="text"
                    name="shipping_first_name"
                    {...register("shipping_first_name")}
                  />
                  <span className="error-message">
                    {errors?.shipping_first_name?.message}
                  </span>
                </div>

                <div className={styles.input_field}>
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="shipping_last_name"
                    {...register("shipping_last_name")}
                  />
                  <span className="error-message">
                    {errors?.shipping_last_name?.message}
                  </span>
                </div>
              </div>

              <div className={styles.input_group}>
                <div className={styles.input_field}>
                  <label>Address</label>
                  <input
                    type="text"
                    name="shipping_address"
                    {...register("shipping_address")}
                  />
                  <span className="error-message">
                    {errors?.shipping_address?.message}
                  </span>
                </div>

                {/* <div className={styles.input_field}>
                <label>Address 2</label>
                <input
                  type="text"
                  name="address_2"
                  placeholder="Address line 2"
                  value={formData.address_2}
                  onChange={handleChange}
                />
              </div> */}
              </div>

              <div className={styles.input_group}>
                <div className={styles.input_field}>
                  <label>City</label>
                  <input
                    type="text"
                    name="shipping_city"
                    {...register("shipping_city")}
                  />
                  <span className="error-message">
                    {errors?.shipping_city?.message}
                  </span>
                </div>

                <div className={styles.input_field}>
                  <label>State</label>
                  <Select
                    onChange={(value) => setValue("shipping_state", value)}
                    value={shipping_state}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a device">
                        <p>{shipping_state}</p>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {states?.map((state, index) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="error-message">
                    {errors?.shipping_state?.message}
                  </span>
                </div>
              </div>

              <div className={styles.input_group}>
                <div className={styles.input_field}>
                  <label>Zip Code</label>
                  <input
                    type="number"
                    name="shipping_zip_code"
                    {...register("shipping_zip_code")}
                  />
                  <span className="error-message">
                    {errors?.shipping_zip_code?.message}
                  </span>
                </div>
                <div className={styles.input_field}></div>
              </div>

              <div className={styles.shipping_info}>
                <h3>Device Info</h3>
                {/* <small>
                  Update Shipping info in patient settings to make changes
                </small> */}
              </div>

              <label>Device Name</label>
              <Select
                onChange={(value) => setValue("device_name", value)}
                value={device_name}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a device">
                    <p>
                      {devices.find((_d) => _d.value === device_name)?.label ||
                        "Select a device"}
                    </p>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {devices?.map((device, index) => (
                    <SelectItem key={device.value} value={device.value}>
                      {device.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="error-message">
                {errors?.device_name?.message}
              </span>
              <div className={styles.input_group_2}>
                <div className={styles.input_field}>
                  <label>Gateway Only</label>

                  <label className={`${styles.switch}`}>
                    <input
                      type="checkbox"
                      name="ship_gateway_only"
                      className={`${styles.input} ${styles.checkbox}`}
                      // checked={formData.require_signature}
                      // onChange={handleCheckBoxChange}
                      {...register("ship_gateway_only")}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>

              <div className={styles.input_group_2}>
                <div className={styles.input_field}>
                  <label>Required Signature</label>

                  <label className={`${styles.switch}`}>
                    <input
                      type="checkbox"
                      name="required_signature"
                      className={`${styles.input} ${styles.checkbox}`}
                      // checked={formData.require_signature}
                      // onChange={handleCheckBoxChange}
                      {...register("required_signature")}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>

              <div className={styles.input_group}>
                <div className={styles.input_field}>
                  <label>Notes</label>
                  <textarea type="text" name="notes" {...register("notes")} />
                </div>
                {/* <div className={styles.input_field}></div> */}
              </div>
            </form>
          )}
        </DialogBody>
        <DialogFooter>
          {step === 2 ? (
            <>
              <Button variant="ghost" onClick={() => onClose()}>
                Cancel
              </Button>
              <Button
                variant="ghost"
                disabled={orderDeviceMutationLoading}
                onClick={() => setStep(1)}
              >
                Previous
              </Button>
              <Button
                onClick={() =>
                  pendingValues && orderNewDeviceMutation(pendingValues)
                }
                loading={orderDeviceMutationLoading}
                disabled={orderDeviceMutationLoading}
              >
                Yes, Place Order
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onClose()}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit(submitHandler)}
                loading={orderDeviceMutationLoading}
                disabled={orderDeviceMutationLoading}
              >
                Order Now
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDeviceModal;
